import { faRocket } from "@fortawesome/free-solid-svg-icons";
import axios, { AxiosError } from "axios";
import { useRef, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { DataResult, State } from "@progress/kendo-data-query";
import { combinedUrls } from "../../../URL/functions";
import { proposedFtlRouteUrls } from "../../../URL/ApplicationUrls";
import { ensureDateOrUndefined } from "../../../Utils/dates";
import PageHeader from "../../../Layout/Header/PageHeader";
import SingleSelectKendoGrid from "../../../Grid/Selectable/SingleSelectKendoGrid";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";
import {
  showNotSelfClosingActionFailureToast,
  showNotSelfClosingActionStartToast,
  showNotSelfClosingActionSuccessToast,
  ToastId,
} from "../../../TransportNetwork/NotificationForActionButton";
import fetchGridData from "../../../Grid/Functions/fetchGridData";
import useSavedState from "../../../Grid/UseSavedState";
import { apiErrorNotificationToast } from "../../../Errors/apiErrorNotificationToast";
import { MdmApiError, mdmApiErrorMessageOrFallbackMessage } from "../../../Errors/MdmApiError";
import { InitialState } from "../../../Grid/InitialDataState";
import { ProposedFtlRoute } from "./ProposedFtlRoute";
import { proposedFtlRouteGridColumns } from "./ProposedFtlRouteGridColumns";

export default function ProposedFtlRouteOverview() {
  const [routeLaunchTriggered, setRouteLaunchTriggered] = useState(false);
  const toastId: ToastId = useRef(null);
  const fetchDataUrl = combinedUrls(proposedFtlRouteUrls.base.apiUrl, "KendoGrid");
  const [currentDataState, setCurrentDataState] = useSavedState(
    "ProposedFtlRouteOverviewGrid_DataState",
    { ...InitialState }
  );
  const [refetchedData, setRefetchedData] = useState<DataResult>();

  const refetchDataAndSetNewDataResult = () => {
    trackPromise<DataResult>(fetchGridData(fetchDataUrl, currentDataState)).then(
      (newDataResult) => {
        setRefetchedData(newDataResult);
      }
    );
  };

  const handleDataState = (newState: State) => {
    setCurrentDataState(newState);
  };

  const triggerLaunch = () => {
    setRouteLaunchTriggered(true);
    toastId.current = showNotSelfClosingActionStartToast("Launching proposed routes");
    axios
      .get(combinedUrls(proposedFtlRouteUrls.base.apiUrl, "TriggerRouteLauncher"))
      .then((response) => {
        const launchedRoutes = response.data;
        showNotSelfClosingActionSuccessToast(
          toastId,
          `Launching ${launchedRoutes.join(", ")} was successful`
        );
        refetchDataAndSetNewDataResult();
      })
      .catch((error: AxiosError<MdmApiError>) =>
        showNotSelfClosingActionFailureToast(
          toastId,
          mdmApiErrorMessageOrFallbackMessage(
            error,
            "Error when trying to launch Routes and/or Projects"
          )
        )
      )
      .finally(() => {
        setRouteLaunchTriggered(false);
      });
  };

  const saveFtlRouteLaunchDate = (route: ProposedFtlRoute): void => {
    axios
      .post(combinedUrls(proposedFtlRouteUrls.apiUrlForRouteWithId(route.RouteId), "/LaunchDate"), {
        LaunchDate: route.LaunchDate,
        ReturnLaunchDate: route.ReturnLaunchDate,
      })
      .then(() => refetchDataAndSetNewDataResult())
      .catch((error: AxiosError<MdmApiError>) => apiErrorNotificationToast(error));
  };

  return (
    <>
      <PageHeader headerText="Proposed FTL Routes" />
      <SingleSelectKendoGrid
        localStorageKey="ProposedFtlRouteOverviewGrid_DataState"
        fetchUrl={fetchDataUrl}
        refetchedData={refetchedData}
        handleDataState={handleDataState}
        mapResults={(route: ProposedFtlRoute) => ({
          ...route,
          LaunchDate: ensureDateOrUndefined(route.LaunchDate),
          ReturnLaunchDate: ensureDateOrUndefined(route.ReturnLaunchDate),
        })}
        columns={proposedFtlRouteGridColumns({
          handleLaunchDateChange: (route) => saveFtlRouteLaunchDate(route),
        })}
        idSelector={(route: ProposedFtlRoute) => route.RouteId}
        toolbarItems={[
          {
            text: "Launch routes",
            icon: faRocket,
            onClick: triggerLaunch,
            disabled: () => routeLaunchTriggered,
            roles: AuthorizationPolicies.RequireCalculationTriggerRole,
          },
        ]}
      />
    </>
  );
}
